import { Alert, AlertTitle, Box, Link } from "@mui/material";
import { FormikErrors } from "formik";
import { useEffect, useRef } from "react";
import { FormField } from "./FormField";

interface FormErrorSummaryProps {
  fields: FormField[];
  /** Form.tsx's validate() returns a NESTED map (Formik shape) — flattened here to list each problem. */
  errors: FormikErrors<Record<string, unknown>>;
  submitCount: number;
}

/**
 * Walk the nested Formik error object to leaf string messages, rebuilding each field's full path in the
 * `licenses[0].state` form the inputs use as their DOM `id` (`[i]` for array indices, `.key` otherwise)
 * so the summary's anchor links resolve.
 */
const flattenErrors = (
  node: unknown,
  prefix = "",
): { path: string; message: string }[] => {
  if (typeof node === "string") {
    return node.length > 0 ? [{ path: prefix, message: node }] : [];
  }
  if (Array.isArray(node)) {
    return node.flatMap((child, i) => flattenErrors(child, `${prefix}[${i}]`));
  }
  if (node && typeof node === "object") {
    return Object.entries(node).flatMap(([key, child]) =>
      flattenErrors(child, prefix ? `${prefix}.${key}` : key),
    );
  }
  return [];
};

/**
 * Lists what a failed submit is waiting on, with a link to each field.
 *
 * Field-level errors say *that* something is wrong once you find it; they cannot say *what* is wrong
 * when the offending field is a screen and a half away. Questionnaire fields are keyed by GUID, so the
 * summary looks each error's label up from the field definitions rather than showing a raw id.
 */
/**
 * Does this message say anything the field does not already say by being marked required?
 *
 * Yup's `.required("Required")` is used across ~every form here, so the summary would otherwise read
 * "Phone Number: Required" directly above a field whose own helper text reads "Required".
 */
const isGenericRequired = (message: string): boolean =>
  /^(required|this field is required|required\.?)$/i.test(message.trim());

const FormErrorSummary = ({
  fields,
  errors,
  submitCount,
}: FormErrorSummaryProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const focusedFor = useRef(0);

  // Labels for the top-level fields, plus every subfield of a field array (keyed by its own id, which
  // is what an error path like `licenses[0].state` ends with). Null when the field has no TEXT label —
  // a checkbox whose label is JSX ("I agree to the <link>Privacy Policy</link>"), say. The summary
  // used to fall back to the field's id there, which put `privacyPolicyAccepted:` in front of a
  // patient; the message alone says what is wrong.
  const labelFor = (path: string): string | null => {
    const root = path.split(/[[.]/)[0];
    const leaf = path.split(".").pop() ?? path;

    const field = fields.find((f) => f.id === root);
    const subfield = field?.subfields?.find((s) => s.id === leaf);

    const label = subfield?.text ?? field?.text ?? field?.header;
    return typeof label === "string" && label.length > 0 ? label : null;
  };

  const describe = (label: string | null, message: string): string => {
    if (label === null) {
      return isGenericRequired(message) ? "A required field is empty" : message;
    }
    return isGenericRequired(message) ? label : `${label}: ${message}`;
  };

  const problems = flattenErrors(errors).map(({ path, message }) => ({
    path,
    message,
    label: labelFor(path),
  }));

  const hasProblems = problems.length > 0;

  useEffect(() => {
    // Formik fills in `errors` a render after submitCount increments, so keying purely on submitCount
    // would fire while this is still rendering null and quietly do nothing.
    if (submitCount > 0 && hasProblems && focusedFor.current !== submitCount) {
      focusedFor.current = submitCount;
      ref.current?.focus();
    }
  }, [submitCount, hasProblems]);

  if (submitCount === 0 || !hasProblems) return null;

  return (
    <Alert
      severity="error"
      variant="outlined"
      role="alert"
      ref={ref}
      tabIndex={-1}
      // Outlined, not the filled pink panel it used to be. This is a POINTER to the fields below,
      // not the error itself — the field is where the error lives, and a heavy block competes with
      // it for the same attention while saying the same thing.
      sx={{
        mb: 3,
        borderColor: "error.light",
        bgcolor: "transparent",
        "& .MuiAlert-message": { py: 0.25 },
      }}
    >
      <AlertTitle sx={{ fontWeight: 600, mb: problems.length > 1 ? 0.5 : 0 }}>
        {problems.length === 1
          ? "One field needs attention"
          : `${problems.length} fields need attention`}
      </AlertTitle>
      <Box
        component="ul"
        sx={{ m: 0, pl: problems.length > 1 ? 2 : 0, listStyle: problems.length > 1 ? "disc" : "none" }}
      >
        {problems.map((problem) => (
          <li key={problem.path}>
            <Link
              href={`#${problem.path}`}
              onClick={(event) => {
                event.preventDefault();
                const field = document.getElementById(problem.path);
                field?.scrollIntoView({ block: "center", behavior: "smooth" });
                field?.focus?.({ preventScroll: true });
              }}
            >
              {/* The field's own message is repeated here ONLY when it says more than "this is
                  empty" — "Phone Number: Required" alongside a field already labelled Required is
                  the same sentence twice. A real constraint ("Must be 10 digits") is worth carrying
                  up, because that one you cannot guess from looking at the field. */}
              {describe(problem.label, problem.message)}
            </Link>
          </li>
        ))}
      </Box>
    </Alert>
  );
};

export default FormErrorSummary;
